import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import api, { registerServiceWorker, onConnectivityChange, getOnlineStatus } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(getOnlineStatus());

  // Register service worker on mount
  useEffect(() => { registerServiceWorker(); }, []);

  // Track online/offline
  useEffect(() => {
    return onConnectivityChange(setOnline);
  }, []);

  // Listen for auth expiry events — sesión caducada o invalidada, redirigir
  // a /login (mismo trato que logout manual: nunca dejar al user en landing
  // promocional tras perder sesión).
  useEffect(() => {
    const handler = () => {
      setUser(null);
      api.clearAuth();
      try {
        const p = window.location.pathname;
        if (p !== '/login' && p !== '/login/') {
          window.history.pushState({}, '', '/login');
          window.location.reload();
        }
      } catch (_) {}
    };
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, []);

  const loadUser = useCallback(async () => {
    try {
      if (!api.token) { setLoading(false); return; }
      const userData = await api.me();
      setUser(userData);
      Sentry.setUser({ id: userData.id, email: userData.email, role: userData.role });
      Sentry.setTag('tenant_id', userData.tenant_id || 'none');
    } catch (err) {
      console.error('Auth load error:', err);
      // Don't clear auth if just offline — user data might be in cache
      if (online) {
        api.clearAuth();
        setUser(null);
        Sentry.setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [online]);

  useEffect(() => { loadUser(); }, [loadUser]);

  // Auto-refresh token before expiry (every 3.5h)
  useEffect(() => {
    if (!user || api.isImpersonating()) return;
    const interval = setInterval(async () => {
      try {
        await api.refresh();
        console.log('Token refreshed');
      } catch (e) {
        console.error('Token refresh failed');
      }
    }, 3.5 * 60 * 60 * 1000); // 3.5 hours
    return () => clearInterval(interval);
  }, [user]);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    // 2FA required — caller (Login page) must finish with complete2FA()
    if (data.requires_2fa) return { requires_2fa: true, tmp_token: data.tmp_token };
    // 2FA setup required (admin obligado pero sin configurar) — caller debe
    // pasar a la vista de setup forzado con setup_token
    if (data.requires_2fa_setup) return { requires_2fa_setup: true, setup_token: data.setup_token, email: data.email };
    setUser(data.user);
    Sentry.setUser({ id: data.user.id, email: data.user.email, role: data.user.role });
    Sentry.setTag('tenant_id', data.user.tenant_id || 'none');
    return data.user;
  };

  // Tras forced 2FA verify exitoso, recibimos los tokens completos + user.
  // Setea user en contexto igual que un login normal.
  const completeForced2FA = async (setup_token, secret, code) => {
    const data = await api.twoFAForcedVerify(setup_token, secret, code);
    setUser(data.user);
    Sentry.setUser({ id: data.user.id, email: data.user.email, role: data.user.role });
    Sentry.setTag('tenant_id', data.user.tenant_id || 'none');
    return data;
  };

  const loginWithGoogle = async (credential) => {
    const data = await api.googleLogin(credential);
    if (data?.requires_2fa) return { requires_2fa: true, tmp_token: data.tmp_token };
    setUser(data.user);
    Sentry.setUser({ id: data.user.id, email: data.user.email, role: data.user.role });
    Sentry.setTag('tenant_id', data.user.tenant_id || 'none');
    return data.user;
  };

  const complete2FA = async (tmp_token, code) => {
    const data = await api.twoFAValidate(tmp_token, code);
    setUser(data.user);
    Sentry.setUser({ id: data.user.id, email: data.user.email, role: data.user.role });
    Sentry.setTag('tenant_id', data.user.tenant_id || 'none');
    return data.user;
  };

  const signupTenant = async (data) => {
    const res = await api.signupTenant(data);
    setUser(res.user);
    Sentry.setUser({ id: res.user.id, email: res.user.email, role: res.user.role });
    Sentry.setTag('tenant_id', res.user.tenant_id || 'none');
    return res;
  };

  // SuperAdmin "Ver como": se recarga la app para que todo el estado (sección,
  // cachés, precarga por rol) se rehaga con el usuario suplantado.
  const impersonation = api.isImpersonating() ? api.impersonationInfo() : null;

  const startImpersonation = async (target) => {
    await api.startImpersonation(target);
    window.location.assign('/');
  };

  const stopImpersonation = () => {
    api.stopImpersonation();
    window.location.assign('/');
  };

  const logout = () => {
    // Durante una suplantación, "Cerrar sesión" vuelve a la sesión del SuperAdmin.
    if (api.isImpersonating()) { stopImpersonation(); return; }
    api.clearAuth();
    setUser(null);
    Sentry.setUser(null);
    // Asegurar redirección a /login (no a landing). Si el user estaba en
    // cualquier ruta autenticada, sin esto el App.jsx mostraría LandingPage
    // por defecto (lo que llevaba al CTA de "Crear empresa", confuso UX).
    try {
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/login/') {
        window.history.pushState({}, '', '/login');
        // Pequeño reload para que App re-evalúe la ruta limpiamente
        window.location.reload();
      }
    } catch (_) {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, complete2FA, completeForced2FA, signupTenant, logout, reloadUser: loadUser, online, impersonation, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
